import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { AvatarStack } from "@/components/common/Avatar";
import { HEALTH_DOT, money } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { SearchInput } from "@/components/common/SearchInput";
import { Plus, KanbanSquare } from "lucide-react";

const MODEL_COLOR = { scrum: "blue", hybrid: "violet", lean: "green" };

export default function Projects() {
  const navigate = useNavigate();
  const { can, canProject, me } = useApp();
  const [statusF, setStatusF] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const [search, setSearch] = useState("");

  const { data: projects, isLoading } = useQuery({ queryKey: ["projects"], queryFn: () => get("/pm/projects") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const filtered = useMemo(() => {
    return (projects || []).filter((p) => {
      if (statusF !== "all" && p.status !== statusF) return false;
      if (mineOnly && !(p.members || []).some((m) => m.user_id === me?.id)) return false;
      if (search && !p.name?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [projects, statusF, mineOnly, search, me]);

  return (
    <div>
      <PageHeader title="Projects" subtitle="Delivery and internal projects across the company.">
        {can("pm", "create") && <Button data-testid="new-project-btn" onClick={() => navigate("/pm/projects/new")}><Plus className="mr-1.5 h-4 w-4" />New Project</Button>}
      </PageHeader>

      <div className="mb-4 flex items-center gap-3">
        <SearchInput testid="project-search" placeholder="Search projects" value={search} onChange={setSearch} />
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-48" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch checked={mineOnly} onCheckedChange={setMineOnly} data-testid="my-projects-toggle" />
          <span className="text-sm text-muted-foreground">My projects only</span>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44" />)}</div>
      ) : (projects || []).length === 0 ? (
        <EmptyState icon={KanbanSquare} title="No projects" subtitle="Create a project to get delivery moving." />
      ) : filtered.length === 0 ? (
        <EmptyState icon={KanbanSquare} title="No matching projects" subtitle="Adjust your search or filters." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
          {filtered.map((p) => {
            const accessible = canProject(p);
            return (
              <button key={p.id} data-testid={`project-card-${p.id}`} disabled={!accessible}
                onClick={() => navigate(`/pm/projects/${p.id}`)}
                className={cn("rounded-xl border border-border bg-card p-4 text-left transition-all",
                  accessible ? "hover:border-primary/40 hover:shadow-sm" : "cursor-not-allowed opacity-60")}>
                <div className="flex items-start justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><KanbanSquare className="h-5 w-5" /></div>
                  <StatusBadge family={MODEL_COLOR[p.operating_model]} label={p.operating_model} />
                </div>
                <div className="mt-3 font-display text-[15px] font-semibold text-foreground">{p.name}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={cn("h-2 w-2 rounded-full", HEALTH_DOT[p.health])} />
                  {can("crm", "view") ? (p.client_name || "Internal") : "—"}
                </div>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary" style={{ width: `${p.progress}%` }} /></div>
                <div className="mt-3 flex items-center justify-between">
                  <AvatarStack users={(p.members || []).map((m) => umap[m.user_id]).filter(Boolean)} />
                  <StatusBadge status={p.status} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
